import ReceivingSession, {
  SCAN_FIELDS,
} from "../../../classes/ReceivingSession";
import VendorShipment, {
  findTrackingLine,
} from "../../../classes/VendorShipment";
import { stampTrackingLine } from "./vendorShipments";
import { decideSlipMatch, slipPOs } from "../../../util/packingSlipMatch";
import { poCore } from "../../../util/vendorOrderNumber";
import { isRealTruck, mergeSessionOrders } from "../../../util/receiving";
import { sortBy, uniq, uniqBy } from "lodash";
import { db } from "../../../boot/firebase";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { sanitize } from "../../../classes/DataStructure";

/**
 * One session: the store's copy when it has one, unless `forceUpdate` asks
 * Firestore again -- that copy may be Recent trucks' one-time read.
 */
export const GET_RECEIVING_SESSION = async (
  { dispatch },
  { sessionID, forceUpdate = false }
) => {
  return dispatch("GET_DOC", {
    converter: ReceivingSession,
    path: ReceivingSession.getPath(sessionID),
    stateKey: "receivingSessions",
    id: sessionID,
    ...(forceUpdate ? { forceUpdate: true } : {}),
  });
};

export const LISTEN_RECEIVING_SESSION = async (
  { dispatch },
  { sessionID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: ReceivingSession,
    path: ReceivingSession.getPath(sessionID),
    stateKey: "receivingSessions",
    id: sessionID,
    next,
    error,
    unlisten,
  });
};

/**
 * Simple converter for scan documents — stores raw data with an id field, and
 * the session the scan belongs to. `receivingScans` holds every session's
 * scans by their own id, and a listener that stops leaves its scans there, so
 * the session is what tells this truck's scans from the last one's. Read from
 * the document's path, never written: RECORD_SCAN keeps to SCAN_FIELDS.
 */
const ScanConverter = {
  toFirestore(scan) {
    return scan;
  },
  fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    data.id = snapshot.id;
    // receivingSessions/{sessionID}/scans/{scanID}
    data.sessionID = snapshot.ref.parent.parent.id;
    return data;
  },
};

export const LISTEN_RECEIVING_SCANS = async (
  { dispatch },
  { sessionID, next, error, unlisten }
) => {
  const path = ReceivingSession.getScanPath(sessionID);
  return dispatch("LISTEN_COLLECTION", {
    converter: ScanConverter,
    stateKey: "receivingScans",
    path,
    next,
    error,
    unlisten,
  });
};

/**
 * Sessions started in the last `days` days, newest first -- the way back into
 * a truck that was marked done too soon, or a session the browser lost.
 */
export const GET_RECENT_RECEIVING_SESSIONS = (
  { dispatch },
  { days = 7 } = {}
) => {
  return dispatch("GET_COLLECTION", {
    converter: ReceivingSession,
    stateKey: "receivingSessions",
    path: ReceivingSession.getPath(),
    conditions: [["startedAt", ">=", new Date(Date.now() - days * 86400e3)]],
    limitCount: 50,
    pagination: { sortBy: "startedAt", descending: true },
  });
};

/**
 * The sessions still open ('scanning'), on one truck when `truckNumber` is
 * given -- whatever their age, unlike Recent trucks. Equality filters only,
 * which Firestore answers by merging its single-field indexes, so no
 * composite index is needed.
 */
export const GET_OPEN_RECEIVING_SESSIONS = (
  { dispatch },
  { truckNumber = null } = {}
) => {
  return dispatch("GET_COLLECTION", {
    converter: ReceivingSession,
    stateKey: "receivingSessions",
    path: ReceivingSession.getPath(),
    conditions: [
      ["status", "==", "scanning"],
      ...(truckNumber ? [["truckNumber", "==", String(truckNumber)]] : []),
    ],
  });
};

/**
 * Create a new receiving session.
 */
export const CREATE_RECEIVING_SESSION = async ({ state }) => {
  const now = new Date();
  const uid = state.user.id;

  const session = new ReceivingSession(null, {
    status: "setup",
    selectedOrders: [],
    startedAt: now,
    startedBy: uid,
  });

  const ref = doc(collection(db, ReceivingSession.getPath()));
  session.id = ref.id;

  await setDoc(ref, ReceivingSession.toFirestore(session));
  return session;
};

/**
 * Update receiving session (e.g., add/remove orders, change status).
 */
export const UPDATE_RECEIVING_SESSION = async (
  { state },
  { sessionID, changes }
) => {
  const path = ReceivingSession.getPath(sessionID);
  return updateDoc(doc(db, path), changes);
};

/**
 * Add POs to a session without dropping any already on it. Every device on a
 * truck adds the POs its own scans name, and writing a device's whole list
 * replaced the session's: whoever wrote last erased what the others had
 * added. Read and written in one transaction, each of `orders` the session
 * lacks (by orderID) is appended and the rest is left as it is. `changes`
 * are written alongside.
 */
export const ADD_RECEIVING_SESSION_ORDERS = async (
  context,
  { sessionID, orders, changes = {} }
) => {
  const ref = doc(db, ReceivingSession.getPath(sessionID));
  return runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    const stored = (snapshot.exists() && snapshot.data().selectedOrders) || [];
    tx.update(ref, {
      ...changes,
      selectedOrders: mergeSessionOrders(stored, orders),
    });
  });
};

/**
 * Start scanning phase — transitions session from 'setup' to 'scanning'.
 */
export const START_RECEIVING_SCAN = async ({ state }, { sessionID }) => {
  const path = ReceivingSession.getPath(sessionID);
  return updateDoc(doc(db, path), { status: "scanning" });
};

/**
 * This session's scans, as plain objects. Read on demand rather than listened
 * to: the callers are the cancel and delete paths, which need to know whether
 * anything was received and — to revert it — what.
 */
export const GET_RECEIVING_SCANS = async (context, { sessionID }) => {
  const snapshot = await getDocs(
    collection(db, ReceivingSession.getScanPath(sessionID))
  );
  return snapshot.docs.map((scan) => ({ id: scan.id, ...scan.data() }));
};

/** A scan that resolved to one of the vendor's own packages. */
const isVendorMatched = (scan) =>
  Boolean(
    scan &&
      scan.matched &&
      scan.vendorShipmentID &&
      scan.vendorLineIndex != null &&
      scan.vendorTrackingIndex != null
  );

/** A Firestore Timestamp, a Date or an ISO string, in ms; NaN for none. */
const toMillis = (value) => {
  if (!value) return NaN;
  if (typeof value.toMillis === "function") return value.toMillis();
  return new Date(value).getTime();
};

/**
 * The order's shipment documents that list this tracking number, the one a
 * receipt belongs on first: a real truck's over Marvin's truck-0 placeholder,
 * then the most recently synced -- the same preference the scanning step
 * receives a package by (preferredShipment in src/util/receiving.js).
 */
const shipmentsListing = (docs, trackingNo) =>
  sortBy(
    docs.filter((shipment) =>
      findTrackingLine(shipment.data().lines, trackingNo)
    ),
    [
      (shipment) => (isRealTruck(shipment.data().truckNumber) ? 0 : 1),
      (shipment) => -(toMillis(shipment.data().lastSynced) || 0),
    ]
  );

/**
 * Stamp a scan's package where it is listed NOW, when the shipment document
 * the scan names no longer lists it. The vendor sync carries a receipt to
 * the document that lists its package once Marvin has moved the load --
 * retiring the truck-0 placeholder that took the dock's 25 scans of SNA06187
 * on 2026-10-05, or refreshing it with the part of the load still waiting
 * (writeShipments in functions/vendorShippingSync.js). The package is found
 * by its tracking number, which is what a matched scan records as its
 * barcode.
 *
 * A record stamps the preferred document (shipmentsListing) and only when
 * the package is not received there yet: a receipt made against the other
 * copy is not this scan's to change. An undo takes back only this scan's own
 * stamp -- same receiver, same moment, as RECORD_SCAN wrote it -- from
 * whichever document carries it, so a copy the sync kept, or a duplicate,
 * listing the same package first cannot hide it.
 */
const stampMovedPackage = async (scan, patch, meta) => {
  const trackingNo = String(scan.barcode || "").trim();
  if (!trackingNo) return { stamped: false, missing: true };
  const snapshot = await getDocs(
    collection(
      db,
      VendorShipment.getPath(scan.projectID, scan.saleID, scan.orderID)
    )
  );
  const homes = shipmentsListing(
    snapshot.docs.filter((shipment) => shipment.id !== scan.vendorShipmentID),
    trackingNo
  );
  if (!homes.length) return { stamped: false, missing: true };

  if (patch.receivedAt) {
    return stampTrackingLine(homes[0].ref, null, null, patch, meta, {
      trackingNo,
      onlyIf: (trackingLine) => !trackingLine.receivedAt,
    });
  }
  let result = { stamped: false, skipped: true };
  for (const home of homes) {
    result = await stampTrackingLine(home.ref, null, null, patch, meta, {
      trackingNo,
      onlyIf: (trackingLine) =>
        trackingLine.receivedBy === scan.scannedBy &&
        toMillis(trackingLine.receivedAt) === toMillis(scan.scannedAt),
    });
    if (result.stamped) return result;
  }
  return result;
};

/**
 * Mark the vendor's package received -- or not, on undo -- for a matched scan.
 *
 * The write is stampTrackingLine's read-modify-write of the whole `lines`
 * array (see vendorShipments.js): the field-path update it replaced, indexed
 * into the array, truncated the shipment document to the one stamped path and
 * took the receiving page down. When the document turns out to be one of
 * those remnants the stamp is skipped and the vendor is asked for a fresh copy
 * instead; the scan itself is already on the session, so nothing is lost.
 * The scan's barcode -- the package's tracking number -- is checked against
 * the package at its indices, which a sync can have moved. When the document
 * is gone, or no longer lists the package -- the load is on another truck
 * now -- the stamp follows the package there (stampMovedPackage).
 */
const stampScanPackage = async (dispatch, scan, patch, meta) => {
  const shipmentPath = VendorShipment.getPath(
    scan.projectID,
    scan.saleID,
    scan.orderID,
    scan.vendorShipmentID
  );
  let result = await stampTrackingLine(
    doc(db, shipmentPath),
    scan.vendorLineIndex,
    scan.vendorTrackingIndex,
    patch,
    meta,
    { trackingNo: String(scan.barcode || "").trim() || null }
  );
  if (result.missing || result.unlisted) {
    result = await stampMovedPackage(scan, patch, meta);
  }
  if (result.corrupted) {
    dispatch("RESYNC_VENDOR_SHIPMENTS", {
      orders: [
        {
          projectID: scan.projectID,
          saleID: scan.saleID,
          orderID: scan.orderID,
          vendorOrderNumber: result.vendorOrderNumber,
        },
      ],
    });
  }
  return result;
};

/**
 * Record a scan — writes immediately to Firestore subcollection.
 * Returns the created scan document reference.
 */
export const RECORD_SCAN = async (
  { state, dispatch },
  { sessionID, scanData }
) => {
  const now = new Date();
  const uid = state.user.id;

  const data = sanitize(scanData, SCAN_FIELDS);
  data.scannedAt = now;
  data.scannedBy = uid;

  const ref = doc(collection(db, ReceivingSession.getScanPath(sessionID)));

  await setDoc(ref, data);

  // Sticky: once a session has received something it is history, even if every
  // scan is later reverted. Written once, not on every scan.
  const session = state.receivingSessions[sessionID];
  if (!session || !session.data.hadScans) {
    await updateDoc(doc(db, ReceivingSession.getPath(sessionID)), {
      hadScans: true,
    });
  }

  // If this is a vendor-matched scan, also update the tracking line
  if (isVendorMatched(data)) {
    await stampScanPackage(
      dispatch,
      data,
      { receivedBy: uid, receivedAt: now, scanStatus: "Received" },
      { modified: now, modifiedBy: uid }
    );
  }

  return { id: ref.id, ...data };
};

/**
 * Delete a scan (undo).
 */
export const DELETE_SCAN = async (
  { state, dispatch },
  { sessionID, scanID, scanData }
) => {
  const ref = doc(db, ReceivingSession.getScanPath(sessionID, scanID));
  await deleteDoc(ref);

  // If this was a vendor-matched scan, also undo the tracking line update
  if (isVendorMatched(scanData)) {
    await stampScanPackage(
      dispatch,
      scanData,
      { receivedBy: null, receivedAt: null, scanStatus: "" },
      { modified: new Date(), modifiedBy: state.user.id }
    );
  }
};

/**
 * Is anything received into this session? One read, because the question is
 * "any?" and not "how many?". Both the cancel and the delete path have to ask
 * for themselves: a caller that skipped the check would leave the vendor's
 * tracking lines saying Received with nothing pointing at them.
 */
const hasScans = async (sessionID) => {
  const snapshot = await getDocs(
    query(collection(db, ReceivingSession.getScanPath(sessionID)), limit(1))
  );
  return !snapshot.empty;
};

/**
 * Take every scan back off a session: each one is undone through DELETE_SCAN,
 * so the vendor's tracking lines stop saying Received too.
 *
 * Sequential on purpose — two scans of the same shipment write the same
 * document, and Firestore's last-write-wins would drop one of the undos.
 */
export const REVERT_RECEIVING_SCANS = async (
  { dispatch },
  { sessionID, scans }
) => {
  const toRevert =
    scans || (await dispatch("GET_RECEIVING_SCANS", { sessionID }));
  for (const scan of toRevert) {
    await dispatch("DELETE_SCAN", {
      sessionID,
      scanID: scan.id,
      scanData: scan,
    });
  }
  return toRevert.length;
};

/**
 * Cancel a receiving session: it is not a truck anyone can come back to, but
 * it stays in the list saying so.
 *
 * Refused while scans are still on it — those marked the vendor's packages
 * received, so they are either reverted (REVERT_RECEIVING_SCANS) or the
 * session is finished properly through the Complete step, which is what
 * actually creates the packing slips.
 */
export const CANCEL_RECEIVING_SESSION = async ({ state }, { sessionID }) => {
  if (await hasScans(sessionID)) throw new Error("sessionHasScans");

  return updateDoc(doc(db, ReceivingSession.getPath(sessionID)), {
    status: "canceled",
    canceledAt: new Date(),
    canceledBy: state.user.id,
  });
};

/**
 * Delete a receiving session.
 *
 * Refused while the session still has scans: recording one stamped the
 * vendor's tracking line `Received`, and the undo for that lives in
 * DELETE_SCAN. Deleting the session would leave those lines received with
 * nothing pointing at them, so the scans have to come off first.
 *
 * firestore.rules lets an admin delete any session, and an employee delete a
 * canceled one — cancelling already proved it holds no receipts.
 */
export const DELETE_RECEIVING_SESSION = async ({ commit }, { sessionID }) => {
  if (await hasScans(sessionID)) throw new Error("sessionHasScans");

  await deleteDoc(doc(db, ReceivingSession.getPath(sessionID)));
  commit("REMOVE", { stateKey: "receivingSessions", id: sessionID });
};

/**
 * Complete receiving session — transitions to 'completed'.
 */
export const COMPLETE_RECEIVING_SESSION = async ({ state }, { sessionID }) => {
  const now = new Date();
  const uid = state.user.id;
  const path = ReceivingSession.getPath(sessionID);
  return updateDoc(doc(db, path), {
    status: "completed",
    completedAt: now,
    completedBy: uid,
  });
};

/**
 * Get all pending vendor shipments (collectionGroup query).
 * "Pending" is now the server-side filter `outstanding > 0` -- the package
 * count writeShipments and stampTrackingLine denormalize onto the document.
 * Documents synced before scripts/backfill-vendor-shipment-outstanding.mjs
 * ran carry no `outstanding` field and are excluded until it does.
 */
export const GET_PENDING_VENDOR_SHIPMENTS = async ({ dispatch }) => {
  return dispatch("GET_COLLECTION", {
    converter: VendorShipment,
    stateKey: "vendorShipments",
    colGroup: "vendorShipments",
    conditions: [["outstanding", ">", 0]],
  });
};

/**
 * Listen to pending vendor shipments (collectionGroup listener). Same
 * outstanding > 0 caveat as GET_PENDING_VENDOR_SHIPMENTS.
 *
 * Shared: the Receiving setup step opens it, and the page opens it again for
 * the session before the setup step goes away. Each holder takes a reference
 * (`retain`) and releases its own handle, so the setup step unmounting does
 * not take the session's listener down with it -- which it did, leaving the
 * scanning step on a frozen copy of the pending shipments: a sync, or another
 * device's scan, arrived in Firestore and never on screen.
 */
export const LISTEN_PENDING_VENDOR_SHIPMENTS = (
  { dispatch },
  { next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: VendorShipment,
    stateKey: "vendorShipments",
    colGroup: "vendorShipments",
    conditions: [["outstanding", ">", 0]],
    listenerPath: "pendingVendorShipments",
    retain: true,
    next,
    error,
    unlisten,
  });
};

/**
 * Every shipment on one truck, regardless of received state. The scanning
 * step's "already received" refusal (ReceivingScanning.handleScan) and the
 * Complete step's expected counts (ReceivingComplete.expectedCountForOrder)
 * need the fully-received shipments that the outstanding > 0 listener drops.
 * Lives on its own stateKey because both listeners write the same docs:
 * receiving a truck shipment's last package drops it to outstanding 0, the
 * pending listener fires `removed`, and a shared key would REMOVE a doc the
 * truck listener still matches (it only sees `modified` and won't re-add).
 * truckNumber is stored as a string by functions/vendorShippingParse.js
 * (`text(header1.TruckNumber)`).
 */
export const LISTEN_TRUCK_VENDOR_SHIPMENTS = (
  { dispatch },
  { truckNumber, next, error, unlisten } = {}
) => {
  const truck = String(truckNumber).trim();
  return dispatch("LISTEN_COLLECTION", {
    converter: VendorShipment,
    stateKey: "truckShipments",
    colGroup: "vendorShipments",
    conditions: [["truckNumber", "==", truck]],
    listenerPath: `vendorShipments/truck/${truck}`,
    next,
    error,
    unlisten,
  });
};

/**
 * Search orders by PO number or vendor name.
 * Queries Firestore collectionGroup for prefix match on orderPO,
 * and also checks already-loaded local orders.
 */
export const SEARCH_ORDERS_BY_PO = async ({ state }, { poNumber }) => {
  const trimmed = poNumber.trim();
  if (!trimmed) return [];
  const normalized = trimmed.toUpperCase();

  // 1. Check already-loaded orders (case-insensitive contains match on PO or vendor)
  const seen = new Set();
  const localMatches = Object.values(state.orders).filter((order) => {
    const orderPO = (order.data.orderPO || "").toUpperCase();
    const composedPO = (
      (order.projectID || "") + (order.saleID || "")
    ).toUpperCase();
    const vendor = (order.data.vendor || "").toUpperCase();
    const match =
      orderPO.includes(normalized) ||
      composedPO.includes(normalized) ||
      vendor.includes(normalized);
    if (match) seen.add(order.id);
    return match;
  });

  // 2. Firestore collectionGroup prefix query on orderPO
  //    orderPO values are stored as-is (mixed case), so query both
  //    the raw input and uppercase version to catch more results.
  const queries = [trimmed];
  if (trimmed !== normalized) queries.push(normalized);
  // Also try lowercase in case POs are stored that way
  const lower = trimmed.toLowerCase();
  if (lower !== trimmed && lower !== normalized) queries.push(lower);

  const remoteMatches = [];
  for (const prefix of queries) {
    const end =
      prefix.slice(0, -1) +
      String.fromCharCode(prefix.charCodeAt(prefix.length - 1) + 1);
    try {
      const snapshot = await getDocs(
        query(
          collectionGroup(db, "orders"),
          where("orderPO", ">=", prefix),
          where("orderPO", "<", end),
          limit(20)
        )
      );

      snapshot.forEach((doc) => {
        if (seen.has(doc.id)) return;
        seen.add(doc.id);
        // Extract projectID and saleID from path:
        // projects/{projectID}/sales/{saleID}/orders/{orderID}
        const segments = doc.ref.path.split("/");
        const projectID = segments[1] || "";
        const saleID = segments[3] || "";
        const data = doc.data();
        remoteMatches.push({
          id: doc.id,
          projectID,
          saleID,
          data: {
            orderPO: data.orderPO || projectID + saleID,
            vendor: data.vendor || "",
          },
        });
      });
    } catch (err) {
      console.warn("Order search query failed:", err.message);
    }
  }

  return [...localMatches, ...remoteMatches];
};

/**
 * Match one extracted slip to a sales order: fetch what its two keys find --
 * orders by PO, acks by vendor order number -- and let
 * util/packingSlipMatch.js decide. Either search failing is not fatal; the
 * decision is made on whatever came back.
 */
const matchOneSlip = async (dispatch, slip) => {
  const poNumber = (slip.poNumber || "").trim();
  const vendorName = (slip.vendorName || "").trim();
  // Both of the batch read's order numbers when its two passes disagreed
  // (functions/extractPackingSlip.js mergeSlipHeader); each is looked up, and
  // orders both find stand side by side for decideSlipMatch.
  const vendorOrderNumbers = uniq(
    [
      ...(Array.isArray(slip.vendorOrderNumbers)
        ? slip.vendorOrderNumbers
        : []),
      slip.vendorOrderNumber,
    ]
      .map((number) => String(number || "").trim())
      .filter(Boolean)
  );
  // Each PO a slip lists is searched (slipPOs). A PO the vendor keyed
  // without its leading zero ("6400017A") is searched as the sale it names
  // too; the prefix search would never reach it.
  const poQueries = uniq(
    slipPOs(poNumber).flatMap((po) => {
      const core = poCore(po);
      return [po, core && core.core !== po.toUpperCase() ? core.core : ""];
    })
  ).filter(Boolean);
  const [byVendorOrder, nearVendorOrder, byPOs] = await Promise.all([
    Promise.all(
      vendorOrderNumbers.map((vendorOrderNumber) =>
        dispatch("SEARCH_ACKS_BY_VENDOR_ORDER", { vendorOrderNumber }).catch(
          () => []
        )
      )
    ),
    Promise.all(
      vendorOrderNumbers.map((vendorOrderNumber) =>
        dispatch("SEARCH_ACKS_NEAR_VENDOR_ORDER", {
          vendorOrderNumber,
          vendorName,
        }).catch(() => [])
      )
    ),
    Promise.all(
      poQueries.map((po) =>
        dispatch("SEARCH_ORDERS_BY_PO", { poNumber: po }).catch(() => [])
      )
    ),
  ]);
  return decideSlipMatch(slip, {
    byVendorOrder: byVendorOrder.flat().filter(Boolean),
    nearVendorOrder: nearVendorOrder.flat().filter(Boolean),
    byPO: uniqBy(byPOs.flat().filter(Boolean), "id"),
  });
};

/**
 * Match a batch of extracted packing slips (from extractPackingSlipBatch) to
 * sales orders. Returns one result per slip, in the same order, each with its
 * matched order (or null) and the candidate orders considered, so the caller
 * can bucket them into matched / needs-assignment.
 */
export const MATCH_PACKING_SLIP_POS = async ({ dispatch }, { slips }) => {
  const results = [];
  for (const slip of slips || []) {
    results.push(await matchOneSlip(dispatch, slip));
  }
  return results;
};
